import { useCallback, useState, type FC } from 'react';

import Image from 'next/image';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';

import type { VoidCallback } from '@/type-utils';
import { classes } from '@/next-utils/css-utils/scss-utils';
import SkeletonLoader from '@/react/components/utility/SkeletonLoader';
import type { ProductModel } from '@/services/models/Product';

import { Price } from '@/react/components/product/Price';
import type IStylable from '@/react/components/traits/IStylable';

import { Link } from '@/react/components/core-ui/Link';
import { getGroupDisplayName } from '@/services/utils/product-utils';

import S from './styles.base.module.scss';

export interface IProductSuggestionTileProps extends IStylable {
  /**
   * The product search suggestion to render.
   */
  suggestion: ProductModel;

  /**
   * A callback function to be called when the tile is clicked.
   *
   * The tile will navigate to the product's URL, so this callback can be used
   * to perform additional actions when the tile is clicked, such as tracking
   * analytics or updating the UI.
   */
  onClick: VoidCallback;
}

/**
 * Product suggestion tile component.
 *
 * Looks like a similar and reduced version of {@link ProductTile} used for PLP's.
 *
 * It's kept as a separate component for semantic reasons and to avoid too much responsibility in the
 * `ProductTile` component, since this one doesn't have a lot of the features that `ProductTile` has.
 *
 * In the future, we might consider merging them, but for now, this separation is useful to keep lean.
 */
export const ProductSuggestionTile: FC<IProductSuggestionTileProps> = ({
  suggestion,
  className,
  onClick
}) => {
  // #region Configuration Properties
  const searchConfig = ConfigurationService.getConfig('search');
  const productConfig = ConfigurationService.getConfig('product');
  // Additional background color from configuration to fulfill the square shape
  // and to avoid the image being stretched.
  //
  // We inherit the background color from the product configuration instead of the search configuration
  // because the product image used in the suggestion tile is a image that is already
  // configured in the product configuration for other places like the product tile and the pdp,
  // so we want to keep the same background color for consistency.
  const backgroundColor = productConfig.getSetting('background.tile').value;
  // Sizes attribute for the next/image to improve performance and responsiveness.
  const sizes = searchConfig.getSetting(
    'searchSuggestions.productSuggestionTile.sizes'
  ).value;
  // We use the same configuration as in `plp` to keep consistency with the product tiles.
  const showGenderOnProductTiles = productConfig.getSetting(
    'plp.showGenderOnProductTiles'
  ).value;
  // #endregion

  // #region State Properties
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);

  const onLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);
  // #endregion

  return (
    <Link
      href={suggestion.getUrlWithCategory()}
      className={classes(S.tile, className)}
      onClick={onClick}
      variant="text"
    >
      <div className={S.imageContainer} style={{ backgroundColor }}>
        {!imageLoaded && <SkeletonLoader mode="light" />}
        <Image
          fill
          sizes={sizes}
          className={classes(S.image, imageLoaded && S.loaded)}
          onLoad={onLoad}
          src={suggestion.primaryImage.src}
          alt={suggestion.name}
        />
      </div>
      <p className={S.title}>
        {showGenderOnProductTiles && (
          <span>
            {getGroupDisplayName(suggestion.gender ?? suggestion.group)}{' '}
          </span>
        )}
        <span>{suggestion.name}</span>
      </p>
      <Price className={S.price} price={suggestion.price} />
    </Link>
  );
};
